import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FieldValues } from "react-hook-form";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useRouter } from "@miplatform/hooks";
import {
  CalendarType,
  IActivity,
  ICalendarConfig,
  ICalendarSet,
  IDateRangeISO,
  IInspection,
  IInspectionAttempt,
  IInspectionTemplate,
  IInspectionTrayConfig,
  ISelectOption,
  ITrade,
  IVerticalTabProps,
  InspectionActionTypes,
} from "@miplatform/types";
import {
  addToDate,
  createUUID,
  getDateISO,
  isBefore,
  isDateAfter,
  isSameDay,
  subtractFromDate,
} from "@miplatform/utils";
import {
  useCalendarSet,
  useInspection,
  useInspectionAttempt,
  useTrades,
} from "../../hooks";
import { useInspectionBoardViewModel } from "./useInspectionBoardViewModel";
import { useInspectionListViewModel } from "./useInspectionListViewModel";

const InspectionsContext = createContext<InspectionsContextType>({
  selectedInspection: null,
  getInspectionDetails: async () => {},
  selectedInspectionAttempt: null,
  setSelectedInspectionAttemptListItem: () => {},
  getInspectionAttemptDetails: async () => {},
  drawerType: null,
  setDrawerType: () => {},
  getJumpRange: () => null,
  showHighlight: false,
  setShowHighlight: () => {},
  onClose: () => {},
  inspectionDrawerOpen: false,
  setInspectionDrawerOpen: () => {},
  inspectionDays: {},
  inspectionList: [],
  inspectionTemplates: {},
  dateRange: { startDate: getDateISO(), endDate: null },
  onDateRangeChange: () => {},
  getInspectionTemplates: async () => {},
  search: "",
  handleSearch: () => {},
  isLoading: false,
  refreshInspections: () => {},
  jumpToInspection: () => {},
  setIsConfirmationModalOpen: () => {},
  isConfirmationModalOpen: false,
  setIsCancelModalOpen: () => {},
  isCancelModalOpen: false,
  calendarSet: null,
  activeCalendar: null,
  changeActiveCalendar: () => {},
  inspectionBuckets: [],
  templateFilterOptions: [],
  getInspectionActivityHistory: async () => [],
  inspectionActivities: [],
  trades: [],
  tradeOptions: [],
  updateInspectionNotes: async () => {},
  cancelInspectionAttempt: async () => {},
});

const getInspectionActivityHistoryUUID = createUUID();

export const InspectionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { params } = useRouter();
  const { calendarSetId, calendarId } = params;

  const { trades, tradeOptions, getTrades } = useTrades();

  const {
    inspectionAttempt: selectedInspectionAttemptDetails,
    cancelInspectionAttempt,
    getInspectionAttempt: getInspectionAttemptDetails,
    clear: clearSelectedInspectionAttemptDetails,
    isLoading: isInspectionAttemptDetailsLoading,
  } = useInspectionAttempt();

  const {
    inspection: selectedInspection,
    getInspection: getInspectionDetails,
    updateInspectionNotes,
  } = useInspection();

  const {
    activeCalendar,
    changeActiveCalendar,
    calendarSet,
    getCalendarSet,
    loading: isCalendarLoading,
  } = useCalendarSet();

  const {
    inspectionDays,
    inspectionTemplates,
    dateRange,
    onDateRangeChange,
    getInspectionDays,
    getInspectionBuckets,
    getInspectionTemplates,
    isLoading,
    search,
    handleSearch,
    inspectionBuckets,
    templateFilterOptions,
    resetBoard,
  } = useInspectionBoardViewModel();

  const { inspectionList, getInspectionList } = useInspectionListViewModel();

  const [
    selectedInspectionAttemptListItem,
    setSelectedInspectionAttemptListItem,
  ] = useState<IInspectionAttempt | null>(null);
  const [drawerType, setDrawerType] = useState<InspectionActionTypes | null>(
    null
  );
  const [inspectionDrawerOpen, setInspectionDrawerOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);
  const [inspectionActivities, setInspectionActivities] = useState<IActivity[]>(
    []
  );

  const stateManager = useComponentState();

  const refreshInspections = useCallback(async () => {
    if (activeCalendar && calendarSet) {
      if (activeCalendar?.calendarType === CalendarType.Kanban) {
        getInspectionDays(dateRange, activeCalendar);
      } else if (activeCalendar?.calendarType === CalendarType.HoldsList) {
        getInspectionList(activeCalendar);
      }

      getInspectionBuckets(calendarSet.eventType, activeCalendar);
    }
  }, [dateRange, activeCalendar?.id, calendarSet?.id]);

  const onClose = (refresh?: boolean) => {
    setInspectionDrawerOpen(false);
    setIsConfirmationModalOpen(false);
    setShowHighlight(false);
    setSelectedInspectionAttemptListItem(null);
    clearSelectedInspectionAttemptDetails();
    if (refresh) refreshInspections();
  };

  const isInDateRange = (date: string, dateRange: IDateRangeISO) => {
    if (!dateRange.endDate) {
      return isSameDay(date, dateRange.startDate);
    }
    return (
      (isBefore(date, dateRange.endDate) ||
        isSameDay(date, dateRange.endDate)) &&
      (isDateAfter(date, dateRange.startDate) ||
        isSameDay(date, dateRange.startDate))
    );
  };

  const getJumpRange = (
    scheduleDate: string,
    dateRange: IDateRangeISO
  ): IDateRangeISO | null => {
    if (isInDateRange(scheduleDate, dateRange)) return null;

    if (!dateRange.endDate) {
      return { startDate: scheduleDate, endDate: null };
    }
    const newStartDate = subtractFromDate(scheduleDate, 1);
    const newEndDate = addToDate(scheduleDate, 1);

    return {
      startDate: getDateISO(newStartDate),
      endDate: getDateISO(newEndDate),
    };
  };

  const jumpToInspection = (newRange: IDateRangeISO) => {
    setShowHighlight(true);
    onDateRangeChange(newRange);
  };

  const getInspectionActivityHistory = async (refId: string) => {
    stateManager.abort(getInspectionActivityHistoryUUID);
    return stateManager.run(async () => {
      const response = await taskService.activities.getByInspectionRefId({
        refId,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setInspectionActivities(response.data);
        return response.data;
      }
      return [];
    }, getInspectionActivityHistoryUUID);
  };

  const selectedInspectionAttempt = useMemo(() => {
    if (!selectedInspectionAttemptListItem) return null;
    if (
      selectedInspectionAttemptDetails?.id ===
      selectedInspectionAttemptListItem.id
    ) {
      return selectedInspectionAttemptDetails;
    }
    return selectedInspectionAttemptListItem;
  }, [
    selectedInspectionAttemptDetails?.id,
    selectedInspectionAttemptListItem?.id,
    JSON.stringify(selectedInspectionAttemptDetails),
  ]);

  const value = useMemo(
    () => ({
      // UI state
      selectedInspectionAttempt,
      setSelectedInspectionAttemptListItem,
      getInspectionAttemptDetails,
      selectedInspection,
      getInspectionDetails,
      drawerType,
      setDrawerType,
      inspectionDrawerOpen,
      setInspectionDrawerOpen,
      showHighlight,
      setShowHighlight,
      onClose,
      isLoading:
        isLoading ||
        isInspectionAttemptDetailsLoading ||
        isCalendarLoading ||
        stateManager.isBusy(),
      jumpToInspection,
      isConfirmationModalOpen,
      setIsConfirmationModalOpen,
      isCancelModalOpen,
      setIsCancelModalOpen,
      getJumpRange,
      templateFilterOptions,
      // Data + filters
      inspectionDays,
      inspectionList,
      inspectionTemplates,
      dateRange,
      onDateRangeChange,
      search,
      handleSearch,
      refreshInspections,
      getInspectionTemplates,
      calendarSet,
      activeCalendar,
      changeActiveCalendar,
      inspectionBuckets,
      getInspectionActivityHistory,
      inspectionActivities,
      trades,
      tradeOptions,
      updateInspectionNotes,
      cancelInspectionAttempt,
    }),
    [
      selectedInspection?.id,
      selectedInspection?.modifiedOn,
      selectedInspectionAttemptListItem?.id,
      selectedInspectionAttemptDetails?.id,
      JSON.stringify(selectedInspectionAttempt),
      drawerType,
      showHighlight,
      inspectionDrawerOpen,
      dateRange,
      inspectionList,
      inspectionDays,
      inspectionTemplates,
      isConfirmationModalOpen,
      calendarSetId,
      calendarSet,
      isLoading,
      activeCalendar?.id,
      inspectionBuckets,
      inspectionActivities,
      isCancelModalOpen,
      trades,
    ]
  );

  useEffect(() => {
    getTrades();
  }, []);

  useEffect(() => {
    getCalendarSet(calendarSetId);
  }, [calendarSetId]);

  useEffect(() => {
    if (selectedInspectionAttemptListItem) {
      getInspectionAttemptDetails(selectedInspectionAttemptListItem.id);
    }
  }, [selectedInspectionAttemptListItem?.id]);

  useEffect(() => {
    refreshInspections();
  }, [activeCalendar?.id, dateRange]);

  useEffect(() => {
    if (calendarId) {
      resetBoard();
      changeActiveCalendar(calendarId);
    }
  }, [calendarId, calendarSet]);

  return (
    <InspectionsContext.Provider value={value}>
      {children}
    </InspectionsContext.Provider>
  );
};

export const useInspectionsContext = () => {
  const context = useContext(InspectionsContext);
  if (!context) {
    throw new Error(
      "useInspectionsContext must be used within a InspectionsProvider"
    );
  }
  return context;
};

interface InspectionsContextType {
  selectedInspectionAttempt: IInspectionAttempt | null;
  setSelectedInspectionAttemptListItem: (
    inspectionAttempt: IInspectionAttempt | null
  ) => void;
  getInspectionAttemptDetails: (attemptId: string) => Promise<void>;
  selectedInspection: IInspection | null;
  getInspectionDetails: (id: string) => Promise<void>;
  drawerType: InspectionActionTypes | null;
  setDrawerType: (type: InspectionActionTypes | null) => void;
  showHighlight: boolean;
  setShowHighlight: (show: boolean) => void;
  onClose: (refresh?: boolean) => void;
  inspectionDrawerOpen: boolean;
  setInspectionDrawerOpen: (open: boolean) => void;
  inspectionDays: Record<string, Record<string, IInspectionAttempt[]>>;
  inspectionList: IInspectionAttempt[];
  inspectionTemplates: Record<string, IInspectionTemplate>;
  dateRange: IDateRangeISO;
  onDateRangeChange: (range: IDateRangeISO) => void;
  getInspectionTemplates: () => Promise<void>;
  search: string;
  handleSearch: (value: string) => void;
  isLoading: boolean;
  refreshInspections: (dateRange?: IDateRangeISO) => void;
  getJumpRange: (
    scheduleDate: string,
    dateRange: IDateRangeISO
  ) => IDateRangeISO | null;
  jumpToInspection: (newRange: IDateRangeISO) => void;
  setIsConfirmationModalOpen: (open: boolean) => void;
  isConfirmationModalOpen: boolean;
  setIsCancelModalOpen: (open: boolean) => void;
  isCancelModalOpen: boolean;
  calendarSet: ICalendarSet | null;
  activeCalendar: ICalendarConfig | null;
  changeActiveCalendar: (calendarId: string) => void;
  inspectionBuckets: IInspectionTrayConfig[];
  templateFilterOptions: IVerticalTabProps<FieldValues>[];
  getInspectionActivityHistory: (attemptRefId: string) => Promise<IActivity[]>;
  inspectionActivities: IActivity[];
  trades: ITrade[];
  tradeOptions: ISelectOption[];
  updateInspectionNotes: ({
    id,
    notes,
    modifiedOn,
  }: {
    id: string;
    notes: string;
    modifiedOn: string;
  }) => Promise<void>;
  cancelInspectionAttempt: ({
    id,
    reason,
  }: {
    id: string;
    reason: string;
  }) => Promise<void>;
}
